/**
 * PDF Storage Utility using IndexedDB
 * Handles storing and retrieving PDFs with larger storage capacity
 */

class PDFStorage {
  constructor() {
    this.dbName = "ContactPDFsDB";
    this.version = 1;
    this.db = null;
  }

  // Initialize IndexedDB
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("pdfs")) {
          const objectStore = db.createObjectStore("pdfs", { keyPath: "id", autoIncrement: true });
          objectStore.createIndex("contactId", "contactId", { unique: false });
          objectStore.createIndex("timestamp", "timestamp", { unique: false });
        }
      };
    });
  }

  // Ensure DB is initialized
  async ensureDB() {
    if (!this.db) {
      await this.init();
    }
    return this.db;
  }

  // Save PDFs for a contact
  async savePDFs(contactId, pdfs) {
    const db = await this.ensureDB();
    const transaction = db.transaction(["pdfs"], "readwrite");
    const store = transaction.objectStore("pdfs");
    const index = store.index("contactId");

    // Delete existing PDFs for this contact
    const deleteRequest = index.openCursor(IDBKeyRange.only(contactId));
    deleteRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    // Save new PDFs
    return new Promise((resolve, reject) => {
      deleteRequest.onsuccess = () => {
        const savePromises = pdfs.map((pdf, index) => {
          return new Promise((resolveSave, rejectSave) => {
            const pdfData = {
              contactId: contactId,
              base64Pdf: pdf.base64Pdf,
              fileName: pdf.fileName || `document_${index + 1}.pdf`,
              timestamp: pdf.timestamp || Date.now(),
              pdfUrl: pdf.pdfUrl || null,
            };
            const addRequest = store.add(pdfData);
            addRequest.onsuccess = () => resolveSave();
            addRequest.onerror = () => rejectSave(addRequest.error);
          });
        });

        Promise.all(savePromises)
          .then(() => {
            // Save metadata in localStorage (lightweight)
            const metadata = pdfs.map((pdf) => ({
              fileName: pdf.fileName || `document_${Date.now()}.pdf`,
              timestamp: pdf.timestamp || Date.now(),
            }));
            localStorage.setItem(`contact_pdfs_meta_${contactId}`, JSON.stringify(metadata));
            resolve();
          })
          .catch(reject);
      };
    });
  }

  // Get PDFs for a contact
  async getPDFs(contactId) {
    const db = await this.ensureDB();
    const transaction = db.transaction(["pdfs"], "readonly");
    const store = transaction.objectStore("pdfs");
    const index = store.index("contactId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(contactId);
      request.onsuccess = () => {
        const pdfs = request.result.map((item) => ({
          base64Pdf: item.base64Pdf,
          fileName: item.fileName,
          timestamp: item.timestamp,
          pdfUrl: item.pdfUrl,
        }));
        // Sort by timestamp
        pdfs.sort((a, b) => a.timestamp - b.timestamp);
        resolve(pdfs);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get PDF count for a contact (from metadata)
  getPDFCount(contactId) {
    try {
      const metadata = localStorage.getItem(`contact_pdfs_meta_${contactId}`);
      if (metadata) {
        const meta = JSON.parse(metadata);
        return meta.length;
      }
    } catch (e) {
      // Fallback to IndexedDB count
    }
    return 0;
  }

  // Delete a PDF by index
  async deletePDF(contactId, index) {
    const db = await this.ensureDB();
    const transaction = db.transaction(["pdfs"], "readwrite");
    const store = transaction.objectStore("pdfs");
    const indexStore = store.index("contactId");

    return new Promise((resolve, reject) => {
      const request = indexStore.getAll(contactId);
      request.onsuccess = () => {
        const pdfs = request.result;
        if (pdfs[index]) {
          const deleteRequest = store.delete(pdfs[index].id);
          deleteRequest.onsuccess = () => {
            // Update metadata
            const metadata = localStorage.getItem(`contact_pdfs_meta_${contactId}`);
            if (metadata) {
              try {
                const meta = JSON.parse(metadata);
                meta.splice(index, 1);
                localStorage.setItem(`contact_pdfs_meta_${contactId}`, JSON.stringify(meta));
              } catch (e) {
                // Ignore metadata update error
              }
            }
            resolve();
          };
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          reject(new Error("PDF not found"));
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Update PDF name
  async updatePDFName(contactId, index, newFileName) {
    const db = await this.ensureDB();
    const transaction = db.transaction(["pdfs"], "readwrite");
    const store = transaction.objectStore("pdfs");
    const indexStore = store.index("contactId");

    return new Promise((resolve, reject) => {
      const request = indexStore.getAll(contactId);
      request.onsuccess = () => {
        const pdfs = request.result;
        if (pdfs[index]) {
          pdfs[index].fileName = newFileName;
          const updateRequest = store.put(pdfs[index]);
          updateRequest.onsuccess = () => {
            // Update metadata
            const metadata = localStorage.getItem(`contact_pdfs_meta_${contactId}`);
            if (metadata) {
              try {
                const meta = JSON.parse(metadata);
                meta[index].fileName = newFileName;
                localStorage.setItem(`contact_pdfs_meta_${contactId}`, JSON.stringify(meta));
              } catch (e) {
                // Ignore metadata update error
              }
            }
            resolve();
          };
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error("PDF not found"));
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Delete all PDFs for a contact
  async deleteAllPDFs(contactId) {
    const db = await this.ensureDB();
    const transaction = db.transaction(["pdfs"], "readwrite");
    const store = transaction.objectStore("pdfs");
    const index = store.index("contactId");

    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(contactId));
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          localStorage.removeItem(`contact_pdfs_meta_${contactId}`);
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
const pdfStorage = new PDFStorage();
export default pdfStorage;

